import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface Role {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleData {
  name: string;
  description?: string;
  permissions: string[];
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
  permissions?: string[];
}

export function useRoles() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => {
    const fetchRoles = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiFetch.get<{ roles: Role[] }>("/api/roles");
        setRoles(data.roles);
      } catch (err) {
        setError(err instanceof ApiError ? err : new Error("Error al cargar roles"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchRoles();
  }, [fetchKey]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  const createRole = useCallback(async (data: CreateRoleData): Promise<Role> => {
    const result = await apiFetch.post<{ role: Role }>("/api/roles", data);
    setRoles((prev) => [...prev, result.role]);
    return result.role;
  }, []);

  const updateRole = useCallback(
    async (roleId: string, data: UpdateRoleData): Promise<Role> => {
      const result = await apiFetch.put<{ role: Role }>(`/api/roles/${roleId}`, data);
      setRoles((prev) => prev.map((r) => (r.id === roleId ? result.role : r)));
      return result.role;
    },
    []
  );

  const deleteRole = useCallback(async (roleId: string): Promise<void> => {
    await apiFetch.delete(`/api/roles/${roleId}`);
    setRoles((prev) => prev.filter((r) => r.id !== roleId));
  }, []);

  return {
    roles,
    isLoading,
    error,
    refetch,
    createRole,
    updateRole,
    deleteRole,
  };
}

export function useRole(roleId: string | undefined) {
  const [role, setRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!roleId) {
      setIsLoading(false);
      return;
    }

    const fetchRole = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiFetch.get<{ role: Role }>(`/api/roles/${roleId}`);
        setRole(data.role);
      } catch (err) {
        setError(err instanceof ApiError ? err : new Error("Error al cargar rol"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchRole();
  }, [roleId]);

  return { role, isLoading, error };
}
